// The practice league's calendar (PLAN-2026-10-30-the-calendar §7): its one
// week, built by the same `leagueAgenda` and `calendarView` the endpoint
// uses, from the simulator's own views. The simulator plays one week, so the
// weeks after it are empty and the screen says so.

import { calendarView, leagueAgenda, periodKind, type StoredPeriod } from '@fsp/clock';
import type { CalendarView, TeamRef } from '@fsp/domain';
import type { Game } from '@fsp/sports-core';
import type { SimState } from './league';
import { tradesView } from './trades';
import { leagueRef, lineupView, periodRef, teamRef } from './views';
import { waiversView } from './waivers';

const iso = (at: number): string => new Date(at).toISOString();

export function calendarSimView(
  state: SimState,
  now: number,
  from: number,
  to: number,
): CalendarView {
  const settings = state.settings;
  const inSeason = state.status !== 'setup';
  const games: Game[] = inSeason
    ? state.proGames.map((g) => ({
        id: g.id,
        sport: 'football',
        season: state.season,
        start: iso(g.startAt),
        homeTeam: g.home,
        awayTeam: g.away,
        status: 'scheduled',
      }))
    : [];
  const week = periodRef(state);
  const periods: StoredPeriod[] = inSeason
    ? [
        {
          season: state.season,
          index: week.index,
          label: week.label,
          start: week.start,
          end: week.end,
          windows: [
            {
              id: `sim-w${String(week.index)}`,
              start: week.start,
              end: week.end,
              firstKickoff:
                games.length === 0
                  ? null
                  : games.reduce((a, b) => (Date.parse(a.start) <= Date.parse(b.start) ? a : b))
                      .start,
            },
          ],
          flags: { playoffEligible: week.kind === 'playoff', proBye: state.byes },
          kind: periodKind(settings, week.index),
        },
      ]
    : [];
  const agenda = leagueAgenda({
    settings,
    periods,
    games,
    draftAt: !inSeason && state.draftAt !== null ? iso(state.draftAt) : null,
    from,
    to,
  });
  const mine = state.teams.find((t) => t.isMine);
  const opponents = new Map<number, { opponent: TeamRef | null; matchupId: string | null }>();
  if (inSeason && mine !== undefined) {
    const lineup = lineupView(state, mine.id, now);
    const game = state.schedule.find(
      (m) => m.period === week.index && (m.home === mine.id || m.away === mine.id),
    );
    if (game !== undefined) {
      const other = state.teams.find(
        (t) => t.id === (game.home === mine.id ? game.away : game.home),
      );
      opponents.set(week.index, {
        opponent: other === undefined ? null : teamRef(state, other, now),
        matchupId: game.id,
      });
    }
    const trades = tradesView(state, now);
    const laid = calendarView({
      agenda,
      mine: {
        teamId: mine.id,
        lineups: [lineup],
        opponents,
        trades: [...trades.forMe, ...trades.sent, ...trades.review],
        claims: waiversView(state, now).myClaims,
        votes: [],
        pickem: [],
        keepersAt: null,
      },
      from,
      to,
    });
    return viewOf(state, now, from, to, mine.id, laid);
  }
  return viewOf(state, now, from, to, null, calendarView({ agenda, mine: null, from, to }));
}

function viewOf(
  state: SimState,
  now: number,
  from: number,
  to: number,
  myTeamId: string | null,
  laid: Pick<CalendarView, 'weeks' | 'events'>,
): CalendarView {
  return {
    league: leagueRef(state),
    from: iso(from),
    to: iso(to),
    timeZone: state.readerZone ?? state.settings.identity.timeZone,
    myTeamId,
    weeks: laid.weeks,
    events: laid.events,
    updatedAt: iso(now),
  };
}
